import { LevelConfig } from '@/models/LearnTypes';

/**
 * Calculate the adjusted XP reward based on completion count
 */
export const getAdjustedXpReward = (
  originalXP: number, 
  completionCount: number
): number => {
  const adjusted = Math.max(1, Math.ceil(originalXP / (completionCount + 1)));
  return adjusted;
};

/**
 * Calculate XP earned for a level completion
 */
export const calculateXP = (
  currentLevel: LevelConfig,
  wordsToReview: string[],
  completionCount: number = 0,
  isLevelPerfect?: boolean // Add optional parameter for explicit perfect tracking
): { xpEarned: number; isPerfect: boolean } => {
  // Get the base XP value from the level configuration
  const originalXP = currentLevel.xpReward;
  
  // Calculate the adjusted XP based on completion count
  const adjustedXP = getAdjustedXpReward(originalXP, completionCount);
  
  console.log(`Level completion calculation: completionCount=${completionCount}, originalXP=${originalXP}, adjustedXP=${adjustedXP}`);
  
  // Determine if the level was completed perfectly
  let isPerfect = false;
  
  // Check for mistake signals from all possible sources
  const matchExerciseIsPerfect = (window as any).__MATCH_EXERCISE_IS_PERFECT__ === true;
  const matchExerciseHadMistake = (window as any).__MATCH_EXERCISE_HAD_MISTAKE__ === true;
  
  // Also check localStorage for mistake tracking
  let storedHadMistake = false;
  try {
    storedHadMistake = localStorage.getItem('match_exercise_had_mistake') === 'true';
  } catch (e) {
    console.error('Error checking localStorage:', e);
  }
  
  // If ANY source indicates a mistake was made, the level is NOT perfect
  const definitelyHadMistake = matchExerciseHadMistake || storedHadMistake;
  
  // Check for presence of failed words in the level completion history
  // Note: If the flag isn't set at all, we assume no failed words
  const currentLevelKey = `level_${currentLevel.id}_had_failed_words`;
  
  // Check if this level has EVER had failed words in this session
  let levelHadFailedWords = false;
  try {
    levelHadFailedWords = localStorage.getItem(currentLevelKey) === 'true';
  } catch (e) {
    console.error('Error checking localStorage for failed words:', e);
  }
  
  // The global flag indicates whether any words were failed during this level attempt
  const hadFailedWords = (window as any).__HAD_RECYCLED_WORDS__ === true || levelHadFailedWords;
  
  // If we have current words to review, this level definitely had failed words
  if (wordsToReview.length > 0) {
    // Update both our global flag and localStorage to ensure perfect tracking persists
    (window as any).__HAD_RECYCLED_WORDS__ = true;
    
    // Store in localStorage so we never lose track of failed words for this level
    try {
      localStorage.setItem(currentLevelKey, 'true');
    } catch (e) {
      console.error('Error saving to localStorage:', e);
    }
  }
  
  // Check if there are no failed cards and if the level was not previously completed
  const noFailedCards = wordsToReview.length === 0;
  const notPreviouslyCompleted = currentLevel.state !== 'completed';
  
  // If we DEFINITELY detected any mistakes, the level is NOT perfect
  if (definitelyHadMistake) {
    isPerfect = false;
  }
  // Otherwise, do the normal logic checks
  else if (isLevelPerfect !== undefined) {
    // A level is perfect if:
    // 1. It was explicitly marked as perfect AND
    // 2. There were no failed words AND
    // 3. There were no failed cards
    isPerfect = isLevelPerfect && !hadFailedWords && noFailedCards;
  } else {
    // Legacy logic for any level type
    // Being more strict now - must have no failed words and no failed cards
    isPerfect = noFailedCards && !hadFailedWords && notPreviouslyCompleted;
  }
  
  // SAFETY OVERRIDES - multiple checks to ensure perfect status is accurate
  
  // OVERRIDE 1: If hadFailedWords is true, the level CANNOT be perfect
  if (hadFailedWords) {
    isPerfect = false;
    
    // Also update the global perfect status flag
    (window as any).__PERFECT_LEVEL_STATUS__ = false;
  }
  
  // OVERRIDE 2: If definitelyHadMistake is true (from match exercise), the level CANNOT be perfect
  if (definitelyHadMistake) {
    isPerfect = false;
    
    // Also update the global perfect status flag
    (window as any).__PERFECT_LEVEL_STATUS__ = false;
  }
  
  // OVERRIDE 3: Check if isPerfectLevel was explicitly set to false
  if (isLevelPerfect === false) {
    isPerfect = false;
  }
  
  let xpEarned = adjustedXP;
  
  // Store the calculated perfect status globally for other components
  (window as any).__CALCULATED_PERFECT_STATUS__ = isPerfect;
  
  if (isPerfect) {
    // Add bonus XP for perfect level completion (not affected by XP decay)
    const perfectBonus = 5; // Fixed 5 XP bonus for perfect level completion
    xpEarned += perfectBonus;
    console.log(`🏆 Perfect level! Adding ${perfectBonus} bonus XP (not affected by completion count)`);
    
    // Also ensure other components can access this information
    try {
      localStorage.setItem('current_level_perfect', 'true');
    } catch (e) {
      console.error('Error saving perfect status to localStorage', e);
    }
  }
  
  console.log(`💰 [useXPCalculation] Final XP calculation: earned=${xpEarned}, isPerfect=${isPerfect}`);
  
  return { xpEarned, isPerfect };
};

/**
 * Calculate the bonus XP for completing an entire lesson perfectly
 * @param isPerfectLesson Whether all levels in the lesson were completed perfectly
 * @returns The bonus XP amount (10 XP for perfect lessons, 0 otherwise)
 */
export const calculateLessonBonus = (isPerfectLesson: boolean): number => {
  if (isPerfectLesson) {
    // CRITICAL FIX: Use consistent bonus amount and log it clearly
    const PERFECT_LESSON_BONUS = 10;
    console.debug(`🏆 [calculateLessonBonus] Perfect lesson! Adding ${PERFECT_LESSON_BONUS} XP bonus`);
    
    // Make this value available globally for consistent usage
    (window as any).__PERFECT_LESSON_BONUS__ = PERFECT_LESSON_BONUS;
    
    return PERFECT_LESSON_BONUS; // Fixed 10 XP bonus for perfect lesson completion
  }
  
  console.debug(`🏆 [calculateLessonBonus] Lesson was not perfect, no bonus awarded`);
  (window as any).__PERFECT_LESSON_BONUS__ = 0;
  return 0;
};
